import dayjs from 'dayjs';

// The Missing Ref Report's date window, read from the range the user last picked
// (localStorage). The sidebar badge doesn't use it: the badge counts all time.

const validRanges = [
  'tomorrow',
  'today',
  'yesterday',
  'nextweek',
  'thisweek',
  'lastweek',
  'nextmonth',
  'thismonth',
  'lastmonth',
  'custom',
];

export const getRange = (): string => {
  try {
    return localStorage.getItem('missing-ref-report-range') || 'thisweek';
  } catch (err) {
    console.error('Error getting range', err);
    return 'thisweek';
  }
};

export const getStart = (range: string): string => {
  try {
    const minDate = dayjs().startOf('day').subtract(1, 'year').format();
    const defaultStart = dayjs().startOf('day').format();
    const localRange = range || localStorage.getItem('missing-ref-report-range');
    const localStart = localStorage.getItem('missing-ref-report-start') || defaultStart;

    if (!localRange || !validRanges.includes(localRange)) {
      return defaultStart;
    }

    // Daily ranges
    if (localRange === 'tomorrow') return dayjs().startOf('day').add(1, 'day').format();
    if (localRange === 'today') return dayjs().startOf('day').format();
    if (localRange === 'yesterday') return dayjs().startOf('day').subtract(1, 'day').format();

    // Weekly ranges
    if (localRange === 'nextweek') return dayjs().add(1, 'week').startOf('week').format();
    if (localRange === 'thisweek') return dayjs().startOf('week').format();
    if (localRange === 'lastweek') return dayjs().subtract(1, 'week').startOf('week').format();

    // Monthly ranges
    if (localRange === 'nextmonth') return dayjs().add(1, 'month').startOf('month').format();
    if (localRange === 'thismonth') return dayjs().startOf('month').format();
    if (localRange === 'lastmonth') return dayjs().subtract(1, 'month').startOf('month').format();

    // Custom range
    if (localRange === 'custom') {
      if (localStart < minDate || localStart === 'Invalid Date') return minDate;
      return localStart;
    }

    return defaultStart;
  } catch (err) {
    console.error('Error getting start date', err);
    return dayjs().startOf('day').format();
  }
};

export const getEnd = (range: string): string => {
  try {
    const maxDate = dayjs().endOf('day').add(1, 'year').format();
    const defaultEnd = dayjs().endOf('day').format();
    const localRange = range || localStorage.getItem('missing-ref-report-range');
    const localEnd = localStorage.getItem('missing-ref-report-end') || defaultEnd;

    if (!localRange || !validRanges.includes(localRange)) {
      return defaultEnd;
    }

    // Daily ranges
    if (localRange === 'tomorrow') return dayjs().endOf('day').add(1, 'day').format();
    if (localRange === 'today') return dayjs().endOf('day').format();
    if (localRange === 'yesterday') return dayjs().endOf('day').subtract(1, 'day').format();

    // Weekly ranges
    if (localRange === 'nextweek') return dayjs().add(1, 'week').endOf('week').format();
    if (localRange === 'thisweek') return dayjs().endOf('week').format();
    if (localRange === 'lastweek') return dayjs().subtract(1, 'week').endOf('week').format();

    // Monthly ranges
    if (localRange === 'nextmonth') return dayjs().add(1, 'month').endOf('month').format();
    if (localRange === 'thismonth') return dayjs().endOf('month').format();
    if (localRange === 'lastmonth') return dayjs().subtract(1, 'month').endOf('month').format();

    // Custom range
    if (localRange === 'custom') {
      if (localEnd > maxDate || localEnd === 'Invalid Date') return maxDate;
      return localEnd;
    }

    return defaultEnd;
  } catch (err) {
    console.error('Error getting end date', err);
    return dayjs().endOf('day').format();
  }
};

/**
 * The date window the report opens on: the range the user last picked
 * (localStorage), else this week.
 */
export const missingRefReportWindow = (): { start: string; end: string } => {
  const range = getRange();
  return { start: getStart(range), end: getEnd(range) };
};
